import type { UseConfirmDialogReturn } from '@vueuse/core'
import { computed, onMounted, ref, watch, type ComputedRef, type defineEmits, type Ref } from 'vue'
import type { Router } from 'vue-router'

export const overlayVisibilityCount = ref(0)

watch(overlayVisibilityCount, (count) => {
  if (count === 1 && !document.body.classList.contains('overflow-hidden')) {
    document.body.classList.add('overflow-hidden')
  } else if (count === 0 && document.body.classList.contains('overflow-hidden')) {
    document.body.classList.remove('overflow-hidden')
  }
})

/* TODO we shouldn't be using query selectors */
export const removeDocumentOverflowHidden = (forceRemove = false) => {
  const appOverlayMasks = document.querySelectorAll('.oen-ui-overlay-mask')
  if (!forceRemove) {
    forceRemove =
      appOverlayMasks.length === 0 && document.body.classList.contains('overflow-hidden')
  }

  if (forceRemove) {
    document.body.classList.remove('overflow-hidden')
  }
}

export const useOverlayRouterGuards = (router: Router) => {
  if (router) {
    router.afterEach(() => {
      removeDocumentOverflowHidden(true)
    })
  }
}

type UseExposeReveal = Ref<UseConfirmDialogReturn<any, any, any>['reveal']>
type UseExposeConfirm = Ref<UseConfirmDialogReturn<any, any, any>['confirm']>
type UseExposeCancel = Ref<UseConfirmDialogReturn<any, any, any>['cancel']>
type UseExposeOnConfirm = Ref<UseConfirmDialogReturn<any, any, any>['onConfirm']>
type UseExposeOnCancel = Ref<UseConfirmDialogReturn<any, any, any>['onCancel']>

export const useExposeOverlay = () => {
  const overlayRef = ref({}) as Ref<UseConfirmDialogReturn<any, any, any>>
  const reveal = ref() as UseExposeReveal
  const confirm = ref() as UseExposeConfirm
  const cancel = ref() as UseExposeCancel
  const isRevealed = computed(() => overlayRef.value.isRevealed)
  const onConfirm = ref() as UseExposeOnConfirm
  const onCancel = ref() as UseExposeOnCancel

  onMounted(() => {
    reveal.value = overlayRef.value.reveal
    confirm.value = overlayRef.value.confirm
    cancel.value = overlayRef.value.cancel
    onConfirm.value = overlayRef.value.onConfirm
    onCancel.value = overlayRef.value.onCancel
  })

  return {
    overlayRef,
    reveal,
    confirm,
    cancel,
    isRevealed,
    onConfirm,
    onCancel,
  }
}

type UseVModelOverlay<T> = {
  props: T
  emits: ReturnType<typeof defineEmits>
  reveal: UseExposeReveal
  cancel: UseExposeCancel
  confirm: UseExposeConfirm
  isRevealed: ComputedRef<ComputedRef<boolean>>
  onConfirm: UseExposeOnConfirm
  onCancel: UseExposeOnCancel
}
export const useVModelOverlay = <T extends Record<string, unknown>>(
  options: UseVModelOverlay<T>
) => {
  const { props, emits, reveal, cancel, isRevealed, onCancel, onConfirm } = options

  onMounted(() => {
    onCancel.value(() => {
      if (props.modelValue) {
        emits('update:modelValue', false)
      }
    })

    onConfirm.value(() => {
      if (props.modelValue) {
        emits('update:modelValue', false)
      }
    })

    watch(
      () => props.modelValue,
      (_newValue, oldValue) => {
        if (props.modelValue) {
          reveal.value()
        } else if (!props.modelValue && oldValue !== undefined && isRevealed?.value) {
          cancel.value()
        }
      },
      {
        immediate: true,
      }
    )
  })
}
